import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import {
  ChannelForm,
  type ChannelFormValues,
} from "@/components/admin/support/channel-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Plus,
  Hash,
  Mail,
  ExternalLink,
  Unplug,
  Pencil,
  Settings2,
  RotateCw,
  Pause,
  Play,
  Trash2,
} from "lucide-react";
import { formatRelativeTime } from "@/lib/format";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import type {
  Channel,
  Mailbox,
  MailboxEmailSyncConfig,
  MailboxSyncTarget,
} from "@db/schema";

type MessageTarget = {
  id: string;
  name: string;
  kind: "folder" | "label";
  system?: boolean;
};

type MailboxTargetsResponse = {
  mailbox: Mailbox;
  targets: MessageTarget[];
  supportsMailboxMutation: boolean;
  missingScopes: string[];
  requiresReconnectForMutation: boolean;
  grantedScopes: string[];
  preferredTargetKind?: "folder" | "label";
};

type MailboxInboundState = "receiving" | "paused" | "needs_reconnect" | "disconnected";

type TicketCleanupAction = "keep" | "soft_delete" | "hard_delete";

type ChannelDeletionPreview = {
  channelId: string;
  ticketCount: number;
  mailboxCount: number;
};

function getMailboxInboundState(mailbox: Mailbox): MailboxInboundState {
  if (!mailbox.nylasAccountId) return "disconnected";
  if (mailbox.status !== "active") return "needs_reconnect";
  if (!mailbox.syncEnabled) return "paused";
  return "receiving";
}

function inboundStateLabel(state: MailboxInboundState): string {
  switch (state) {
    case "receiving":
      return "Receiving tickets";
    case "paused":
      return "Inbound paused";
    case "needs_reconnect":
      return "Needs reconnect";
    case "disconnected":
      return "Disconnected";
  }
}

export default function ChannelsPage() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [disconnectTarget, setDisconnectTarget] = useState<Mailbox | null>(
    null,
  );
  const [deleteMailboxTarget, setDeleteMailboxTarget] = useState<Mailbox | null>(
    null,
  );
  const [deleteChannelTarget, setDeleteChannelTarget] = useState<Channel | null>(
    null,
  );
  const [ticketCleanupAction, setTicketCleanupAction] =
    useState<TicketCleanupAction>("soft_delete");

  const { data: channels, isLoading } = useQuery<Channel[]>({
    queryKey: ["/api/support/channels"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: deletionPreview, isLoading: deletionPreviewLoading } =
    useQuery<ChannelDeletionPreview>({
      queryKey: deleteChannelTarget
        ? [`/api/support/channels/${deleteChannelTarget.id}/deletion-preview`]
        : ["channel-deletion-preview-disabled"],
      queryFn: getQueryFn({ on401: "throw" }),
      enabled: !!deleteChannelTarget,
    });

  const createMutation = useMutation({
    mutationFn: async (data: ChannelFormValues) => {
      const res = await apiRequest("POST", "/api/support/channels", data);
      return (await res.json()) as Channel;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/support/channels"] });
      setDialogOpen(false);
      toast({ title: "Channel created successfully" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to create channel",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: ChannelFormValues;
    }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/support/channels/${id}`,
        data,
      );
      return (await res.json()) as Channel;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/support/channels"] });
      setDialogOpen(false);
      setEditingChannel(null);
      toast({ title: "Channel updated successfully" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to update channel",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function invalidateChannelQueries(channelId?: string) {
    queryClient.invalidateQueries({ queryKey: ["/api/support/channels"] });
    if (channelId) {
      queryClient.invalidateQueries({
        queryKey: [`/api/support/channels/${channelId}/mailboxes`],
      });
      return;
    }
    channels?.forEach((ch) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/support/channels/${ch.id}/mailboxes`],
      });
    });
  }

  const disconnectMutation = useMutation({
    mutationFn: async (mailboxId: string) => {
      await apiRequest("DELETE", `/api/support/mailboxes/${mailboxId}/disconnect`);
    },
    onSuccess: () => {
      invalidateChannelQueries();
      setDisconnectTarget(null);
      toast({ title: "Mailbox disconnected" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to disconnect mailbox",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteMailboxMutation = useMutation({
    mutationFn: async (mailboxId: string) => {
      await apiRequest("DELETE", `/api/support/mailboxes/${mailboxId}`);
    },
    onSuccess: () => {
      invalidateChannelQueries(deleteMailboxTarget?.channelId ?? undefined);
      setDeleteMailboxTarget(null);
      toast({ title: "Mailbox removed" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to remove mailbox",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteChannelMutation = useMutation({
    mutationFn: async ({
      channelId,
      ticketAction,
    }: {
      channelId: string;
      ticketAction: TicketCleanupAction;
    }) => {
      const res = await apiRequest(
        "DELETE",
        `/api/support/channels/${channelId}`,
        { ticketAction },
      );
      return (await res.json()) as {
        message: string;
        ticketsAffected: number;
        ticketAction: TicketCleanupAction;
      };
    },
    onSuccess: (result) => {
      invalidateChannelQueries();
      setDeleteChannelTarget(null);
      setTicketCleanupAction("soft_delete");
      toast({
        title: "Channel deleted",
        description:
          result.ticketsAffected > 0
            ? `${result.ticketsAffected} ticket(s) handled via ${result.ticketAction.replace("_", " ")}.`
            : undefined,
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to delete channel",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function handleOpenCreate() {
    setEditingChannel(null);
    setDialogOpen(true);
  }

  function handleOpenEdit(channel: Channel) {
    setEditingChannel(channel);
    setDialogOpen(true);
  }

  function handleFormSubmit(data: ChannelFormValues) {
    if (editingChannel) {
      updateMutation.mutate({ id: editingChannel.id, data });
    } else {
      createMutation.mutate(data);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Channels"
        description="Manage support channels and connected mailboxes"
        action={
          <Button onClick={handleOpenCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Channel
          </Button>
        }
      />

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[160px] rounded-lg" />
          ))}
        </div>
      ) : !channels?.length ? (
        <EmptyState
          icon={Hash}
          title="No channels"
          description="Create your first channel to start receiving support tickets."
          action={
            <Button onClick={handleOpenCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add Channel
            </Button>
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {channels.map((channel) => (
            <ChannelCard
              key={channel.id}
              channel={channel}
              onEdit={() => handleOpenEdit(channel)}
              onDeleteChannel={() => {
                setTicketCleanupAction("soft_delete");
                setDeleteChannelTarget(channel);
              }}
              onConnectMailbox={async () => {
                try {
                  const res = await apiRequest(
                    "POST",
                    "/api/support/mailboxes/connect",
                    { channelId: channel.id },
                  );
                  const { authUrl } = (await res.json()) as {
                    authUrl: string;
                  };
                  window.open(authUrl, "_blank");
                } catch (err) {
                  toast({
                    title: "Failed to connect mailbox",
                    description:
                      err instanceof Error ? err.message : "Unknown error",
                    variant: "destructive",
                  });
                }
              }}
              onDisconnectMailbox={(mailbox) => setDisconnectTarget(mailbox)}
              onDeleteMailbox={(mailbox) => setDeleteMailboxTarget(mailbox)}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setDialogOpen(false);
            setEditingChannel(null);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingChannel ? "Edit Channel" : "Add Channel"}
            </DialogTitle>
            <DialogDescription>
              {editingChannel
                ? "Update the channel details below."
                : "Fill in the details to create a new channel."}
            </DialogDescription>
          </DialogHeader>
          <ChannelForm
            key={editingChannel?.id ?? "new"}
            defaultValues={
              editingChannel
                ? {
                  name: editingChannel.name,
                  description: editingChannel.description ?? "",
                }
                : undefined
            }
            onSubmit={handleFormSubmit}
            isPending={createMutation.isPending || updateMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Disconnect Confirmation */}
      <ConfirmDialog
        open={!!disconnectTarget}
        title="Disconnect Mailbox"
        description={`Are you sure you want to disconnect "${disconnectTarget?.emailAddress}"? This stops inbound ticket creation and removes the Nylas connection. The mailbox record is kept so you can reconnect later.`}
        confirmLabel="Disconnect"
        destructive
        onConfirm={() =>
          disconnectTarget && disconnectMutation.mutate(disconnectTarget.id)
        }
        onCancel={() => setDisconnectTarget(null)}
      />

      {/* Delete Mailbox Confirmation */}
      <ConfirmDialog
        open={!!deleteMailboxTarget}
        title="Remove Mailbox"
        description={`Permanently remove "${deleteMailboxTarget?.emailAddress}" from this channel? Tickets stay on the channel until you delete the channel.`}
        confirmLabel="Remove Mailbox"
        destructive
        onConfirm={() =>
          deleteMailboxTarget &&
          deleteMailboxMutation.mutate(deleteMailboxTarget.id)
        }
        onCancel={() => setDeleteMailboxTarget(null)}
      />

      {/* Delete Channel Dialog */}
      <Dialog
        open={!!deleteChannelTarget}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteChannelTarget(null);
            setTicketCleanupAction("soft_delete");
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Delete Channel</DialogTitle>
            <DialogDescription>
              Delete "{deleteChannelTarget?.name}" and all of its mailboxes.
              Choose what happens to tickets on this channel.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {deletionPreviewLoading ? (
              <Skeleton className="h-16 rounded-lg" />
            ) : (
              <Alert>
                <AlertDescription>
                  This channel has {deletionPreview?.mailboxCount ?? 0} mailbox(es)
                  and {deletionPreview?.ticketCount ?? 0} ticket(s).
                </AlertDescription>
              </Alert>
            )}

            <RadioGroup
              value={ticketCleanupAction}
              onValueChange={(value) =>
                setTicketCleanupAction(value as TicketCleanupAction)
              }
              className="space-y-3"
            >
              <div className="flex items-start gap-3 rounded-md border p-3">
                <RadioGroupItem value="soft_delete" id="ticket-soft-delete" className="mt-0.5" />
                <div className="space-y-0.5">
                  <Label htmlFor="ticket-soft-delete">Move tickets to Deleted</Label>
                  <p className="text-xs text-muted-foreground">
                    Soft-delete tickets so they appear under Deleted and can be reviewed later.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-md border p-3">
                <RadioGroupItem value="keep" id="ticket-keep" className="mt-0.5" />
                <div className="space-y-0.5">
                  <Label htmlFor="ticket-keep">Keep tickets</Label>
                  <p className="text-xs text-muted-foreground">
                    Leave tickets in the system without a channel assignment.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-md border p-3">
                <RadioGroupItem value="hard_delete" id="ticket-hard-delete" className="mt-0.5" />
                <div className="space-y-0.5">
                  <Label htmlFor="ticket-hard-delete">Permanently delete tickets</Label>
                  <p className="text-xs text-muted-foreground">
                    Irreversibly remove tickets and their internal notes from the database.
                  </p>
                </div>
              </div>
            </RadioGroup>

            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setDeleteChannelTarget(null);
                  setTicketCleanupAction("soft_delete");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deleteChannelMutation.isPending || deletionPreviewLoading}
                onClick={() =>
                  deleteChannelTarget &&
                  deleteChannelMutation.mutate({
                    channelId: deleteChannelTarget.id,
                    ticketAction: ticketCleanupAction,
                  })
                }
              >
                Delete Channel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Channel Card sub-component ---

interface ChannelCardProps {
  channel: Channel;
  onEdit: () => void;
  onDeleteChannel: () => void;
  onConnectMailbox: () => void;
  onDisconnectMailbox: (mailbox: Mailbox) => void;
  onDeleteMailbox: (mailbox: Mailbox) => void;
}

function ChannelCard({
  channel,
  onEdit,
  onDeleteChannel,
  onConnectMailbox,
  onDisconnectMailbox,
  onDeleteMailbox,
}: ChannelCardProps) {
  const { toast } = useToast();
  const [editingMailbox, setEditingMailbox] = useState<Mailbox | null>(null);
  const { data: mailboxes } = useQuery<Mailbox[]>({
    queryKey: [`/api/support/channels/${channel.id}/mailboxes`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: messageTargetData, isLoading: targetsLoading } =
    useQuery<MailboxTargetsResponse>({
      queryKey: editingMailbox
        ? [`/api/support/mailboxes/${editingMailbox.id}/message-targets`]
        : ["mailbox-targets-disabled"],
      queryFn: getQueryFn({ on401: "throw" }),
      enabled: !!editingMailbox,
    });

  const updateMailboxMutation = useMutation({
    mutationFn: async ({
      mailboxId,
      emailSyncConfig,
    }: {
      mailboxId: string;
      emailSyncConfig: MailboxEmailSyncConfig;
    }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/support/mailboxes/${mailboxId}`,
        { emailSyncConfig },
      );
      return (await res.json()) as Mailbox;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/support/channels/${channel.id}/mailboxes`],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/support/channels"] });
      toast({ title: "Mailbox sync settings updated" });
      setEditingMailbox(null);
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to update mailbox sync",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const inboundMutation = useMutation({
    mutationFn: async ({
      mailboxId,
      syncEnabled,
    }: {
      mailboxId: string;
      syncEnabled: boolean;
    }) => {
      const res = await apiRequest(
        "PATCH",
        `/api/support/mailboxes/${mailboxId}`,
        { syncEnabled },
      );
      return (await res.json()) as Mailbox;
    },
    onSuccess: (_mailbox, variables) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/support/channels/${channel.id}/mailboxes`],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/support/channels"] });
      toast({
        title: variables.syncEnabled
          ? "Inbound ticket intake resumed"
          : "Inbound ticket intake paused",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to update inbound intake",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const reconnectMutation = useMutation({
    mutationFn: async (mailboxId: string) => {
      const res = await apiRequest(
        "POST",
        `/api/support/mailboxes/${mailboxId}/reconnect`,
      );
      return (await res.json()) as { authUrl: string };
    },
    onSuccess: ({ authUrl }) => {
      window.open(authUrl, "_blank");
      const refreshMailboxQueries = () => {
        queryClient.invalidateQueries({
          queryKey: [`/api/support/channels/${channel.id}/mailboxes`],
        });
        queryClient.invalidateQueries({ queryKey: ["/api/support/channels"] });
        if (editingMailbox) {
          queryClient.invalidateQueries({
            queryKey: [`/api/support/mailboxes/${editingMailbox.id}/message-targets`],
          });
        }
      };

      refreshMailboxQueries();
      let attempts = 0;
      const interval = window.setInterval(() => {
        attempts += 1;
        refreshMailboxQueries();
        if (attempts >= 15) {
          window.clearInterval(interval);
        }
      }, 2000);
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to start mailbox reconnect",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
                {channel.name}
              </CardTitle>
              {channel.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {channel.description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive"
                onClick={onDeleteChannel}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Accordion type="single" collapsible>
            <AccordionItem value="mailboxes" className="border-b-0">
              <AccordionTrigger className="py-2 text-sm">
                <span className="flex items-center gap-2">
                  <Mail className="h-3.5 w-3.5" />
                  Mailboxes ({mailboxes?.length ?? 0})
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <TooltipProvider delayDuration={200}>
                  <div className="space-y-2">
                    {mailboxes?.map((mb) => {
                      const hasNylasGrant = Boolean(mb.nylasAccountId);
                      const statusSyncEnabled =
                        hasNylasGrant &&
                        ((mb.emailSyncConfig as MailboxEmailSyncConfig | null)?.enabled ??
                          false);
                      const inboundState = getMailboxInboundState(mb);
                      const canPause = inboundState === "receiving";
                      const canResume = inboundState === "paused";
                      const needsReconnect =
                        inboundState === "needs_reconnect" ||
                        inboundState === "disconnected";

                      return (
                        <div
                          key={mb.id}
                          className="flex items-center justify-between rounded-md border p-2 text-sm"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate">
                              {mb.emailAddress ?? "Unknown"}
                            </p>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              <Badge
                                variant="outline"
                                className={
                                  inboundState === "receiving"
                                    ? "bg-green-500/10 text-green-700 border-green-500/20"
                                    : inboundState === "paused"
                                      ? "bg-amber-500/10 text-amber-700 border-amber-500/20"
                                      : "bg-gray-500/10 text-gray-700 border-gray-500/20"
                                }
                              >
                                {inboundStateLabel(inboundState)}
                              </Badge>
                              {hasNylasGrant && (
                                <Badge variant="outline">
                                  Status sync {statusSyncEnabled ? "on" : "off"}
                                </Badge>
                              )}
                              {mb.lastSyncAt && (
                                <span>
                                  Synced {formatRelativeTime(mb.lastSyncAt)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="ml-3 flex items-center gap-1">
                            {canPause && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 shrink-0"
                                    disabled={inboundMutation.isPending}
                                    onClick={() =>
                                      inboundMutation.mutate({
                                        mailboxId: mb.id,
                                        syncEnabled: false,
                                      })
                                    }
                                  >
                                    <Pause className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Pause inbound tickets</TooltipContent>
                              </Tooltip>
                            )}
                            {canResume && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 shrink-0"
                                    disabled={inboundMutation.isPending}
                                    onClick={() =>
                                      inboundMutation.mutate({
                                        mailboxId: mb.id,
                                        syncEnabled: true,
                                      })
                                    }
                                  >
                                    <Play className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Resume inbound tickets</TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 shrink-0"
                                  disabled={!hasNylasGrant}
                                  onClick={() => setEditingMailbox(mb)}
                                >
                                  <Settings2 className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                Ticket status sync settings
                              </TooltipContent>
                            </Tooltip>
                            {needsReconnect && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 shrink-0"
                                    onClick={() => reconnectMutation.mutate(mb.id)}
                                  >
                                    <RotateCw className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Reconnect mailbox</TooltipContent>
                              </Tooltip>
                            )}
                            {hasNylasGrant && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 shrink-0 text-destructive"
                                    onClick={() => onDisconnectMailbox(mb)}
                                  >
                                    <Unplug className="h-3.5 w-3.5" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Disconnect mailbox</TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 shrink-0 text-destructive"
                                  onClick={() => onDeleteMailbox(mb)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Remove mailbox</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}

                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 w-full"
                      onClick={onConnectMailbox}
                    >
                      <ExternalLink className="mr-2 h-3.5 w-3.5" />
                      Connect Mailbox
                    </Button>
                  </div>
                </TooltipProvider>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
      </Card>

      <MailboxSyncDialog
        mailbox={editingMailbox}
        data={messageTargetData}
        isLoading={targetsLoading}
        isSaving={updateMailboxMutation.isPending}
        onClose={() => setEditingMailbox(null)}
        onReconnect={(mailboxId) => reconnectMutation.mutate(mailboxId)}
        onSave={(mailboxId, emailSyncConfig) =>
          updateMailboxMutation.mutate({ mailboxId, emailSyncConfig })
        }
      />
    </>
  );
}

interface MailboxSyncDialogProps {
  mailbox: Mailbox | null;
  data?: MailboxTargetsResponse;
  isLoading: boolean;
  isSaving: boolean;
  onClose: () => void;
  onReconnect: (mailboxId: string) => void;
  onSave: (mailboxId: string, emailSyncConfig: MailboxEmailSyncConfig) => void;
}

function MailboxSyncDialog({
  mailbox,
  data,
  isLoading,
  isSaving,
  onClose,
  onReconnect,
  onSave,
}: MailboxSyncDialogProps) {
  const config = (mailbox?.emailSyncConfig as MailboxEmailSyncConfig | null) ?? null;
  const preferredKind = data?.preferredTargetKind;
  const targets = data?.targets ?? [];

  const [enabled, setEnabled] = useState(config?.enabled ?? false);
  const [markUnreadOnInboundCustomerReply, setMarkUnreadOnInboundCustomerReply] =
    useState(config?.markUnreadOnInboundCustomerReply ?? true);
  const [selectedTargets, setSelectedTargets] = useState<
    Partial<Record<"new" | "in_progress" | "pending" | "closed", string>>
  >({
    new: config?.statusTargets.new ? encodeTarget(config.statusTargets.new) : "",
    in_progress: config?.statusTargets.in_progress
      ? encodeTarget(config.statusTargets.in_progress)
      : "",
    pending: config?.statusTargets.pending
      ? encodeTarget(config.statusTargets.pending)
      : "",
    closed: config?.statusTargets.closed
      ? encodeTarget(config.statusTargets.closed)
      : "",
  });

  useEffect(() => {
    const nextConfig = (mailbox?.emailSyncConfig as MailboxEmailSyncConfig | null) ?? null;
    setEnabled(nextConfig?.enabled ?? false);
    setMarkUnreadOnInboundCustomerReply(
      nextConfig?.markUnreadOnInboundCustomerReply ?? true,
    );
    setSelectedTargets({
      new: nextConfig?.statusTargets.new ? encodeTarget(nextConfig.statusTargets.new) : "",
      in_progress: nextConfig?.statusTargets.in_progress
        ? encodeTarget(nextConfig.statusTargets.in_progress)
        : "",
      pending: nextConfig?.statusTargets.pending
        ? encodeTarget(nextConfig.statusTargets.pending)
        : "",
      closed: nextConfig?.statusTargets.closed
        ? encodeTarget(nextConfig.statusTargets.closed)
        : "",
    });
  }, [mailbox]);

  if (!mailbox) return null;
  const mailboxId = mailbox.id;

  const requiresReconnect = data?.requiresReconnectForMutation ?? false;
  const canSave = !enabled || (
    !requiresReconnect &&
    ["new", "in_progress", "pending", "closed"].every(
      (status) => Boolean(selectedTargets[status as keyof typeof selectedTargets]),
    )
  );

  function handleOpenChange(open: boolean) {
    if (!open) onClose();
  }

  function handleTargetChange(
    status: "new" | "in_progress" | "pending" | "closed",
    value: string,
  ) {
    setSelectedTargets((current) => ({ ...current, [status]: value }));
  }

  function handleSave() {
    const statusTargets = Object.fromEntries(
      Object.entries(selectedTargets)
        .map(([status, value]) => [status, decodeTarget(targets, value || "")])
        .filter((entry): entry is [string, MailboxSyncTarget] => Boolean(entry[1])),
    ) as MailboxEmailSyncConfig["statusTargets"];

    onSave(mailboxId, {
      enabled,
      statusTargets,
      markReadOnStatuses: ["in_progress", "pending", "closed"],
      markUnreadOnInboundCustomerReply,
    });
  }

  return (
    <Dialog open={!!mailbox} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ticket Status Sync Settings</DialogTitle>
          <DialogDescription>
            Map ticket statuses to Nylas folders or labels for {mailbox.emailAddress}.
            This controls outbound mailbox organization, not whether new emails create tickets.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <p className="text-sm font-medium">Enable ticket-to-mailbox status sync</p>
              <p className="text-xs text-muted-foreground">
                Status changes will move thread messages in Nylas and mark them read when appropriate.
                Pausing inbound ticket intake is controlled from the mailbox list.
              </p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>

          {isLoading ? (
            <Skeleton className="h-48 rounded-lg" />
          ) : (
            <>
              {requiresReconnect && (
                <Alert>
                  <AlertDescription className="flex items-center justify-between gap-4">
                    <span>
                      This mailbox needs to be reconnected before Nylas write actions can be used.
                      {data?.missingScopes?.length
                        ? ` Missing scopes: ${data.missingScopes.join(", ")}.`
                        : ""}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onReconnect(mailboxId)}
                    >
                      <RotateCw className="mr-2 h-3.5 w-3.5" />
                      Reconnect
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-4 rounded-md border p-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Status target mappings</p>
                  <p className="text-xs text-muted-foreground">
                    {preferredKind === "label"
                      ? "This mailbox prefers labels."
                      : preferredKind === "folder"
                        ? "This mailbox prefers folders."
                        : "Choose the folder or label to apply for each ticket status."}
                  </p>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  {(["new", "in_progress", "pending", "closed"] as const).map((status) => (
                    <div key={status} className="space-y-1.5">
                      <Label className="capitalize">
                        {status.replace("_", " ")}
                      </Label>
                      <Select
                        value={selectedTargets[status] || undefined}
                        onValueChange={(value) => handleTargetChange(status, value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select target" />
                        </SelectTrigger>
                        <SelectContent>
                          {targets.map((target) => (
                            <SelectItem
                              key={`${target.kind}:${target.id}`}
                              value={`${target.kind}:${target.id}`}
                            >
                              [{target.kind}] {target.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <p className="text-sm font-medium">Mark unread on inbound customer reply</p>
                  <p className="text-xs text-muted-foreground">
                    Used for closed and pending tickets that receive a new client reply.
                  </p>
                </div>
                <Switch
                  checked={markUnreadOnInboundCustomerReply}
                  onCheckedChange={setMarkUnreadOnInboundCustomerReply}
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave || isSaving || isLoading}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function encodeTarget(target: MailboxSyncTarget): string {
  return `${target.kind}:${target.id}`;
}

function decodeTarget(
  targets: MessageTarget[],
  value: string,
): MailboxSyncTarget | null {
  const [kind, id] = value.split(":");
  if (!kind || !id) return null;
  const target = targets.find((entry) => entry.kind === kind && entry.id === id);
  if (!target) return null;
  return { id: target.id, name: target.name, kind: target.kind };
}
